import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { renderCompactZoneBlock } from '../../utils/zoneUtils';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

const ZoneDistributionStrava = ({ userId, fileId, sport }) => {
  const { t } = useTranslation();
  const [heartRateData, setHeartRateData] = useState([]);
  const [paceData,      setPaceData]      = useState([]);
  const [powerData,     setPowerData]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);

  useEffect(() => {
    if (!userId || !fileId) { setLoading(false); return; }
    const fetchData = async () => {
      try {
        const parsedRes = await axios.get(
          `${API_BASE_URL}/api/fit/parsed-data/${fileId}`,
          { params: { userId } }
        );
        const records = parsedRes.data?.records || [];
        const session = parsedRes.data?.session  || {};
        if (!records.length) { setLoading(false); return; }

        const [fcRes, vpRes, cpRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/api/athletes/training-zones/${userId}/FC`),
          axios.get(`${API_BASE_URL}/api/athletes/training-zones/${userId}/VP`),
          axios.get(`${API_BASE_URL}/api/athletes/training-zones/${userId}/CP`),
        ]);
        const fcZone = fcRes.status === 'fulfilled' ? fcRes.value.data : null;
        const vpZone = vpRes.status === 'fulfilled' ? vpRes.value.data : null;
        const cpZone = cpRes.status === 'fulfilled' ? cpRes.value.data : null;
        if (fcZone?.zones?.length) {
          setHeartRateData(calcularDistribucionRecords(records, fcZone.zones, 'heart_rate'));
        }

        const sportNorm = sport || session?.sport || '';
        const isRunning = ['correr','running','Run','VirtualRun','TrailRun'].includes(sportNorm);
        if (isRunning && vpZone?.zones?.length) {
          setPaceData(calcularDistribucionPaceRecords(records, vpZone.zones));
        }

        const isCycling = ['ciclismo','cycling','Ride','VirtualRide'].includes(sportNorm);
        if (isCycling && cpZone?.zones?.length) {
          const hasPower = records.some(r => r.power != null && r.power > 0);
          if (hasPower) setPowerData(calcularDistribucionRecords(records, cpZone.zones, 'power'));
        }

      } catch (err) {
        console.error('ZoneDistributionStrava error:', err.message);
        setError(t('athleteDashboard.zonesLoadingError'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, fileId, sport, t]);

  if (loading) return <p>{t('athleteDashboard.zonesLoading')}</p>;
  if (error)   return <p style={{ color: 'red' }}>{error}</p>;
  if (!heartRateData.length && !paceData.length && !powerData.length) return null;

  return (
    <div className="zone-distribution-chart">
      <h3 style={{ color: '#193255', fontSize: 14, fontWeight: 700 }}>
        {t('athleteDashboard.zonesSummary')}
      </h3>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
        {heartRateData.length > 0 &&
          renderCompactZoneBlock(heartRateData, t('athleteDashboard.zonesHR'), t, 'zoneFC')}
        {paceData.length > 0 &&
          renderCompactZoneBlock(paceData, t('athleteDashboard.zonesPace'), t, 'zoneVP')}
        {powerData.length > 0 &&
          renderCompactZoneBlock(powerData, t('athleteDashboard.zonesCyclingPower'), t, 'zoneCP')}
      </div>
    </div>
  );
};

function parseZoneValue(v) {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.includes(':')) {
    const parts = v.split(':').map(Number);
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return parseFloat(v) || 0;
}

function calcularDistribucionRecords(records, zonas, campo) {
  const zonaTiempo = Array(zonas.length).fill(0);
  let subUmbral = 0;
  records.forEach(record => {
    const valor = record[campo];
    if (valor == null || isNaN(valor) || valor <= 0) return;
    let matched = false;
    for (let i = 0; i < zonas.length; i++) {
      const min = parseZoneValue(zonas[i].min);
      const max = parseZoneValue(zonas[i].max);
      const esUltima = i === zonas.length - 1;
      if ((valor >= min && valor < max) || (esUltima && valor >= min && valor <= max)) {
        zonaTiempo[i] += 1;
        matched = true;
        break;
      }
    }
    if (!matched) subUmbral += 1;
  });
  const total = zonaTiempo.reduce((a, b) => a + b, 0) + subUmbral || 1;
  return zonas.map((z, i) => ({
    name:          z.name || `Z${i + 1}`,
    seconds:       zonaTiempo[i],
    timeFormatted: formatSeconds(zonaTiempo[i]),
    percentage:    parseFloat(((zonaTiempo[i] / total) * 100).toFixed(1)),
  }));
}

function calcularDistribucionPaceRecords(records, zonas) {
  const recs = records
    .filter(r => r.enhanced_speed != null && r.enhanced_speed > 0)
    .map(r => ({ ...r, paceSecPerKm: 1000 / r.enhanced_speed }));
  return calcularDistribucionRecords(recs, zonas, 'paceSecPerKm');
}

function formatSeconds(secs) {
  if (!secs) return '00:00:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return String(h).padStart(2,'0')+':'+String(m).padStart(2,'0')+':'+String(s).padStart(2,'0');
}

export default ZoneDistributionStrava;
